import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";

const DailyDespatchReport = () => {
  const [groups, setGroups] = useState([]); // [{job_id, consignee_name, ..., despatches:[...]}]
  const [loading, setLoading] = useState(false);

  // Filter state
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [despatchDate, setDespatchDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [consigneeId, setConsigneeId] = useState("");

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (despatchDate) payload.despatch_date = despatchDate;
      if (consigneeId) payload.consignee_id = consigneeId;

      const response = await post("/reports/daily_despatch_report/list", payload);
      if (response.status === 200) {
        setGroups(response.data || []);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoading(false);
    }
  }, [showAlert, despatchDate, consigneeId]);

  const fetchConsigneeData = useCallback(async () => {
    try {
      const response = await post("/masters/consignees/list");
      if (response.status === 200) {
        setConsigneeOptions([
          { value: "", label: "Select Consignee" },
          ...response.data.map((item) => ({
            value: item.consignees_id,
            label: item.consignees_name,
          })),
        ]);
      }
    } catch (err) {
      console.log(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchConsigneeData();
  }, [fetchData, fetchConsigneeData]);

  // Print export: browser print of the report card only
  const handlePrint = () => {
    window.print();
  };

  const fmt = (v) => (v === null || v === undefined || v === "" ? "-" : v);
  const sum = (rows, key) =>
  rows.reduce((total, row) => total + (parseFloat(row[key]) || 0), 0);
  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center" id="despatch-report-page-header">
        <div>
          <div className="page-header-title">Daily Despatch Report - Party Wise</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3" id="despatch-report-filters">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Despatch Date</label>
              <input
                type="date"
                className="form-control"
                value={despatchDate}
                onChange={(e) => setDespatchDate(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Consignee</label>
              <select
                className="form-select"
                value={consigneeId}
                onChange={(e) => setConsigneeId(e.target.value)}
              >
                {consigneeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" onClick={fetchData} disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </button>
            </div>
            <div className="col-md-2">
              <button className="btn btn-outline-secondary w-100" onClick={handlePrint} disabled={loading || groups.length === 0}>
                Print / Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Grouped report */}
      <div className="card mt-3" id="despatch-report-print-area">
        <div className="card-body">
          {/* Only visible when printing, since the real page header is hidden then */}
          <h5 className="d-none d-print-block mb-3">
            Daily Despatch Report - Party Wise{" "}
            {despatchDate ? `(${despatchDate})` : ""}
          </h5>

          {loading && <div className="text-center py-4">Loading...</div>}

          {!loading && groups.length === 0 && (
            <div className="text-center text-muted py-4">No despatch records found.</div>
          )}

          {!loading &&
            groups.map((g) => (
              <div key={g.job_id} className="table-responsive mb-4">
                <table className="table table-bordered align-middle mb-0">
                  <thead>
                    {/* Group header row 1: Consignee / Shipper / Line / Cargo / Total Bales / Total Weight */}
                    <tr className="table-primary">
                      <th colSpan={2}>Consignee Name: {fmt(g.consignee_code)}</th>
                      <th colSpan={2}>Shipper Name: {fmt(g.shipper_short_name)}</th>
                      <th colSpan={2}>Line: {fmt(g.liner_short_name)}</th>
                      <th colSpan={3}>Cargo: {fmt(g.cargo_short_desc)}</th>
                      <th>Total Bales: {fmt(g.total_bales)}</th>
                      <th colSpan={2}>Total Weight: {fmt(g.total_weight)}</th>
                    </tr>
                    {/* Group header row 2: POL / BE No / BE Date / Lot No */}
                    <tr className="table-light">
                      <th colSpan={3}>POL: {fmt(g.pol)}</th>
                      <th colSpan={3}>BE No: {fmt(g.be_number)}</th>
                      <th colSpan={3}>BE Date: {fmt(g.be_date)}</th>
                      <th colSpan={2}>Lot No: {fmt(g.lot_no)}</th>
                    </tr>
                    {/* Despatch table column headers */}
                    <tr>
                      <th>S.No</th>
                      <th>Lorry No</th>
                      <th>Container No.</th>
                      <th>Packages</th>
                      <th>Weight</th>
                      <th>Freight</th>
                      <th>Cost Per Ton</th>
                      <th>E-Way Bill No</th>
                      <th>GC No</th>
                      <th>GC Date</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.despatches.map((d) => (
                      <tr key={d.s_no}>
                        <td>{d.s_no}</td>
                        <td>{fmt(d.lorry_number)}</td>
                        <td>{fmt(d.container_no)}</td>
                        <td>{fmt(d.packages)}</td>
                        <td>{fmt(d.weight)}</td>
                        <td>{fmt(d.freight)}</td>
                        <td>{fmt(d.cost_per_ton)}</td>
                        <td>{fmt(d.e_way_bill_no)}</td>
                        <td>{fmt(d.gc_no)}</td>
                        <td>{fmt(d.gc_date)}</td>
                        <td>{fmt(d.remarks)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th></th>
                      <th></th>
                      <th>Total</th>
                      <th>{fmt(Math.round(sum(g.despatches, "packages") * 1000) / 1000)}</th>
                      <th>{fmt(Math.round(sum(g.despatches, "weight") * 1000) / 1000)}</th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
        </div>
      </div>

            <style>{`
        /* Force browsers to print background colors/text colors as-is
           instead of stripping them for ink-saving defaults. Applies on
           screen too (harmless) so the print preview matches this exactly. */
        #despatch-report-print-area * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
 
        @media print {
          /* Isolate the report: hide everything on the page (including the
             app's own sidebar/header/nav that lives outside this component),
             then explicitly reveal only the print area.
 
             NOTE: visibility:hidden keeps hidden elements in the layout
             flow (just invisible), so if the rest of the app is taller
             than one page it silently pushes out a blank trailing page.
             Collapsing html/body height (below) stops that, without
             affecting #despatch-report-print-area since it's taken out
             of normal flow via position:absolute. */
          html, body {
            height: 0 !important;
            overflow: hidden !important;
          }
          body * {
            visibility: hidden !important;
          }
          /* Guard against an app wrapper (e.g. a fixed-height, scrollable
             main-content container) with overflow:hidden/auto clipping the
             absolutely-positioned print area if that wrapper happens to be
             a positioned ancestor. */
          body *:not(html):not(body) {
            overflow: visible !important;
          }
          #despatch-report-print-area,
          #despatch-report-print-area * {
            visibility: visible !important;
          }
          #despatch-report-print-area {
            position: absolute !important;
            top: 0;
            left: 0;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            box-shadow: none !important;
            overflow: visible !important;
            height: auto !important;
          }
          #despatch-report-print-area .card-body {
            padding: 0 !important;
          }
 
          #despatch-report-page-header,
          #despatch-report-filters {
            display: none !important;
          }
 
          .table-responsive {
            overflow: visible !important;
          }
 
          /* Some apps ship a global "stacked/mobile card" table style
             (table, tr, td forced to display:block below a breakpoint) for
             small screens. Print rendering can pick that up too, which
             collapses every column onto its own line. Force real table
             layout here so nothing else can override it. */
          #despatch-report-print-area table {
            display: table !important;
            width: 100% !important;
            border-collapse: collapse !important;
          }
          #despatch-report-print-area thead {
            display: table-header-group !important;
          }
          #despatch-report-print-area tbody {
            display: table-row-group !important;
          }
          #despatch-report-print-area tr {
            display: table-row !important;
          }
          #despatch-report-print-area th,
          #despatch-report-print-area td {
            display: table-cell !important;
            vertical-align: middle !important;
          }
 
          /* Deliberately NOT overriding table/th/td colors, borders, or
             background here — the goal is to reproduce the on-screen
             design (blue group-header bands, light sub-header row, thin
             grey borders) exactly, not flatten it to plain black/white. */
          #despatch-report-print-area table {
            width: 100% !important;
          }
 
          /* Group header tables (Consignee/Shipper/Line/Cargo/Totals, and
             POL/BE/Lot No) — keep them glued to the despatch table below. */
          #despatch-report-print-area .mb-4 > div {
            margin-bottom: 0 !important;
          }
 
          /* Keep each job's group table together where possible, and avoid
             splitting a row across a page break. */
          .table-responsive {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          tr {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          thead {
            display: table-header-group;
          }
 
          @page {
            size: landscape;
            margin: 10mm;
          }
 
          /* Keep content compact enough to fit even if the browser/driver
             ignores the @page landscape hint above (some "Print to PDF"
             drivers do) — see note below the button in the UI. */
          #despatch-report-print-area {
            font-size: 11px;
          }
          #despatch-report-print-area th,
          #despatch-report-print-area td {
            padding: 3px 5px !important;
            white-space: nowrap;
          }
 
          /* Customtable hides its desktop table below the md breakpoint
             (d-none d-md-block) for responsive screens. Force it visible
             when printing regardless of viewport width. */
          #despatch-report-print-area .d-none.d-md-block {
            display: block !important;
          }
 
          /* Customtable's Columns/Filters/Export controls are interactive
             UI, not part of the printed report — hide them. */
          #despatch-report-print-area button {
            display: none !important;
          }
        }
      `}</style>

    </>
  );
};

export default DailyDespatchReport;